import React from 'react';
import {
  TextField,
  BooleanField,
  SimpleShowLayout,
  useRecordContext,
  useResourceContext,
  TabbedShowLayout,
  TabbedShowLayoutTabs,
  Tab,
} from 'react-admin';
import TimestampFields from 'components/TimestampFields';
import { Ten99Section, W2LocalSection, W2TravelSection } from './W2And1099SubSettingsShow';
import LinkField from 'components/LinkField';
import RateMarkupField from 'components/RateMarkupField';
import UsingBlendedRateField from 'components/UsingBlendedRateField';
import { MinimumWageSection } from './MinimumWageSection';
import { W2BurdensSection } from './W2BurdensSection';
import { FeesSection } from './FeesSection';

const hiddenStyle = { display: 'none' };

const getLevelPrefix = (resource: string) => {
  if (resource === 'LocationRateSetting') {
    return 'location';
  } else if (resource === 'MarketRateSetting') {
    return 'market';
  } else if (resource === 'MarketLocationTypeRateSetting') {
    return 'market_location_type';
  } else if (resource === 'StateLocationTypeRateSetting') {
    return 'state_location_type';
  }
  return '';
};

const RateSettingsShow = () => {
  const record = useRecordContext();
  const resource = useResourceContext();

  if (!record) {
    return null;
  }
  const {
    user_id,
    market_id,
    state_id,
    location_id,
    agency_id,
    location_type_id,
    location_type,
    state,
    market,
    location,
    agency,
  } = record;

  const isStateLevel = resource === 'StateRateSetting';
  const isLocationLevel = resource === 'LocationRateSetting';
  const isAgencyLevel = resource === 'AgencyLocationRateSetting';
  const showSettings = isStateLevel || isAgencyLevel;
  const settingsField = showSettings ? 'settings' : `${getLevelPrefix(resource)}_rate_setting`;
  const isW2 = location?.primary_billing_group?.plan === 'enterprise_w2';

  return (
    <SimpleShowLayout>
      <TabbedShowLayout tabs={<TabbedShowLayoutTabs variant="scrollable" />}>
        <Tab label="General">
          <TextField label="ID" source="id" />
          {market_id && <LinkField source="market_id" />}
          {market?.display_name && <TextField label="Market" source="market.display_name" />}
          {location_id && <LinkField label="Location ID" source="location_id" />}
          {location?.name && <TextField label="Location" source="location.name" />}
          {agency_id && <LinkField label="Agency ID" source="agency_id" />}
          {agency?.name && <TextField label="Agency" source="agency.name" />}
          {user_id && <LinkField source="user_id" />}
          {state_id && <LinkField source="state_id" />}
          {state?.name && <TextField label="State" source="state.name" />}
          {location_type_id && (
            <LinkField source="location_type_id" to={`/LocationType/${location_type_id}`} />
          )}
          {location_type?.label && <TextField label="Location Type" source="location_type.label" />}
          <BooleanField source="active" />
          <BooleanField label="Track Unpaid Breaks" source="settings.track_unpaid_breaks" />

          <UsingBlendedRateField
            label="Rate Structure"
            showInheritanceLabel
            variant="rate-structure"
          />

          {(isStateLevel || isLocationLevel) && <RateMarkupField hasBlendedRateMarkup={true} />}

          {isLocationLevel && (
            <TextField
              label="Rate Structure Acknowledgement Status"
              source={`${settingsField}.rate_structure_acknowledgement_status`}
            />
          )}

          <TimestampFields />

          {isStateLevel && <MinimumWageSection settingsField={settingsField} />}

          {(isStateLevel || isLocationLevel) && <W2BurdensSection settingsField={settingsField} />}

          {isStateLevel && <FeesSection settingsField={settingsField} />}
        </Tab>
        <Tab label="1099" style={isW2 && isLocationLevel ? hiddenStyle : {}}>
          <Ten99Section source={settingsField} showTitle={false} />
        </Tab>
        <Tab label="W2 Local" style={!isW2 && isLocationLevel ? hiddenStyle : {}}>
          <W2LocalSection source={settingsField} showTitle={false} />
        </Tab>
        <Tab label="W2 Travel">
          <W2TravelSection source={settingsField} showTitle={false} />
        </Tab>
      </TabbedShowLayout>
    </SimpleShowLayout>
  );
};

export default RateSettingsShow;
